import { coalesceMicrotask } from '@workbuddy/utils/signals';

export interface BatchedKeyLoaderOptions<K, V> {

  /** Resolve a batch of keys in a single call. Return one value per resolvable key, in any order. */
  fetch: (keys: K[]) => Promise<V[]>;

  /** The key a returned value belongs to, so a batch result can be dealt back to the right callers. */
  keyOf: (value: V) => K;

  /**
   * Value for a key the fetch returned nothing for (or when the batch fails). Omit to resolve undefined.
   * Cached only in the first case — a fallback handed out after a failure is never cached.
   */
  fallback?: (key: K) => V;

  /** Cache entry lifetime in ms; an expired entry refetches on the next get. Omit to keep until clear(). */
  ttlMs?: number;

  /** Serialise a non-primitive key for the internal maps. Defaults to the key itself. */
  cacheKey?: (key: K) => unknown;
}

/**
 * Deferred, batched, cached per-key loader. Callers ask for one key at a time (`get`); keys requested
 * within the same microtask are coalesced into one `fetch`, and each result is cached and dealt back to
 * every caller that asked for it. Framework-agnostic — pair it with any fetch (an entity command, an
 * HTTP call, …).
 *
 * Built for lazy per-row grid cells: each visible cell asks for its own key, the loader turns a
 * screenful of asks into one request, and a scroll-back is served from cache.
 */
export class BatchedKeyLoader<K, V> {

  private readonly cache = new Map<unknown, { value: V; at: number }>();
  private pending = new Map<unknown, K>();
  private resolvers = new Map<unknown, ((value: V) => void)[]>();

  private readonly scheduleFlush = coalesceMicrotask(() => this.flush());

  constructor(private readonly options: BatchedKeyLoaderOptions<K, V>) {}

  public get(key: K): Promise<V> {

    const id = this.idOf(key);
    const cached = this.cache.get(id);
    if (cached && !this.isExpired(cached.at)) return Promise.resolve(cached.value);

    return new Promise<V>(resolve => {
      const waiting = this.resolvers.get(id) ?? [];
      waiting.push(resolve);
      this.resolvers.set(id, waiting);
      this.pending.set(id, key);
      this.scheduleFlush();
    });
  }

  /**
   * Read a non-expired cache entry without scheduling a fetch.
   * Returns undefined when missing or past ttl.
   */
  public peek(key: K): V | undefined {

    const cached = this.cache.get(this.idOf(key));
    if (!cached || this.isExpired(cached.at)) return undefined;
    return cached.value;
  }

  /** Write/overwrite a cache entry (optimistic patch). Does not notify callers. */
  public set(key: K, value: V): void {

    this.cache.set(this.idOf(key), { value, at: Date.now() });
  }

  /**
   * Drop cached values so the next `get` refetches.
   * Omit `key` to clear the whole cache; pass a key to drop only that entry.
   */
  public clear(key?: K): void {

    if (key === undefined) {
      this.cache.clear();
      return;
    }
    this.cache.delete(this.idOf(key));
  }

  private idOf(key: K): unknown {

    return this.options.cacheKey ? this.options.cacheKey(key) : key;
  }

  private isExpired(at: number): boolean {

    return this.options.ttlMs != null && (Date.now() - at) > this.options.ttlMs;
  }

  private async flush(): Promise<void> {

    const pending = this.pending;
    const resolvers = this.resolvers;
    this.pending = new Map();
    this.resolvers = new Map();

    if (!pending.size) return;

    const values = await this.fetch([...pending.values()]);
    const failed = values === null;
    const byId = new Map((values ?? []).map(value => [this.idOf(this.options.keyOf(value)), value] as const));

    const at = Date.now();
    for (const [id, key] of pending) {
      const value = byId.get(id) ?? this.options.fallback?.(key);
      // A key the fetch simply had no row for is cached — that answer is real, and refetching it on
      // every read would undo the batching. A failed batch caches nothing, so one blip cannot pin its
      // fallback in place until the ttl runs out; the next read retries instead.
      if (!failed && value !== undefined) this.cache.set(id, { value, at });
      resolvers.get(id)?.forEach(resolve => resolve(value as V));
    }
  }

  /** Null distinguishes a failed batch from one that returned nothing for a key. */
  private async fetch(keys: K[]): Promise<V[] | null> {

    try {
      return (await this.options.fetch(keys)) ?? [];
    } catch {
      // A failed batch resolves its callers to the fallback rather than leaving them pending forever.
      return null;
    }
  }
}
